import React, { useState, useEffect, useRef } from 'react';
import ScannerForm from '../components/ScannerForm.js';
import ScannerCamera from '../components/ScannerCamera.js';
import ScannedResults from '../components/ScannedResults.js';
import BulkProcessing from '../components/BulkProcessing.js';
import { processImage, processWithNLP } from '../services/visionService.js';
import SuccessPopup from '../components/popups/SuccessPopup.js';
import { resizeImage, enhanceImage } from '../utils.js';


function Scanner() {
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [scanDescription, setScanDescription] = useState('');
  const [scannedItems, setScannedItems] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState('');
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [needsDescription, setNeedsDescription] = useState(false);


  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
const [successMessage, setSuccessMessage] = useState('');
const scannedResultsRef = useRef(null);


  // Load items from localStorage when component mounts
  useEffect(() => {
    try {
      const savedItems = localStorage.getItem('scannedItems');
      if (savedItems) {
        const parsedItems = JSON.parse(savedItems);
        if (parsedItems && parsedItems.length > 0) {
          setScannedItems(parsedItems);
          console.log('Loaded items from localStorage:', parsedItems.length);
        }
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
  }, []);

  // Save items to localStorage whenever they change
  useEffect(() => {
    try {
      if (scannedItems && scannedItems.length > 0) {
        console.log('Saving items to localStorage:', scannedItems.length);
        localStorage.setItem('scannedItems', JSON.stringify(scannedItems));
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [scannedItems]);

  // Start the camera - now doesn't need a description
  const startScanning = () => {
    setIsCameraActive(true);
    setProcessingError('');
  };
  const scrollToResults = () => {
    if (scannedResultsRef.current) {
      setTimeout(() => {
        scannedResultsRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start'
        });
      }, 300);
    }
  };
  
  const handleBulkProcessingStart = (description, files) => {
    setScanDescription(description);
    setNeedsDescription(false);
    setIsBulkProcessing(true);
    setProcessingProgress(0);
  };
  // Handle image capture from camera
// In Scanner.js
const handleImageCapture = async (imageData) => {
    setCapturedImage(imageData);
    setIsCameraActive(false);
    
    // Check if on desktop
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (!isMobile) {
      // Apply extra enhancement for desktop webcam images
      try {
        const enhancedImage = await enhanceImage(imageData);
        setCapturedImage(enhancedImage);
      } catch (error) {
        console.error('Error enhancing image:', error);
      }
    }
    
    setNeedsDescription(true);
  };
  
  // Handle description submission after image capture
  const handleDescriptionSubmit = (description) => {
    setScanDescription(description);
    setNeedsDescription(false);
    // Process the image with the description
    handleImageProcessing(description, capturedImage);
  };
  
  // Handle image upload with description
  const handleImageUpload = (description, imageData) => {
    handleImageProcessing(description, imageData);
  };
  
  // Process image with description - now defined!
// In Scanner.js - Update the handleImageProcessing function
const handleImageProcessing = async (description, imageData) => {
    setIsProcessing(true);
    setProcessingError('');
    
    try {
      console.log('Starting image processing with description:', description);
      
      // Check if on desktop
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      console.log(`Processing image on ${isMobile ? 'mobile' : 'desktop'} platform`);
      
      // Process the image differently based on platform
      let processedImage;
      if (isMobile) {
        // For mobile, just resize
        processedImage = await resizeImage(imageData);
      } else {
        // For desktop, enhance and then resize
        const enhancedImage = await enhanceImage(imageData);
        processedImage = await resizeImage(enhancedImage);
      }
      
      console.log('Image processed successfully');
      
      // Process the resized image with Google Vision
      // Use processedImage instead of resizedImage
      const recognizedText = await processImage(processedImage);
      console.log('Raw recognized text:', recognizedText);
      
      if (recognizedText && recognizedText.trim()) {
        // Process the text with NLP
        console.log('Sending text to NLP with description:', description);
        try {
          const processedText = await processWithNLP(recognizedText, description);
          console.log('NLP result:', processedText);
          
          // More detailed handling of NLP results
          if (processedText && 
              processedText.trim() && 
              !processedText.includes("No specific pattern matched") &&
              !processedText.includes(`No ${description} found`) &&
              processedText !== "No pattern matched") {
            
            // Add the processed text to results
            const newItem = {
              id: Date.now(),
              description: description,
              text: processedText,
              timestamp: new Date().toLocaleString()
            };
            
            setScannedItems(prevItems => [newItem, ...prevItems]);
            setCapturedImage(null);
            
            // Show success popup
            setSuccessMessage(`Successfully extracted ${description}: ${processedText}`);
            setShowSuccessPopup(true);
            setNeedsDescription(false);
            setIsCameraActive(false);
            
            // Scroll to results after popup closes
            setTimeout(() => {
              scrollToResults();
            }, 1500);
          } else {
            setProcessingError(`No ${description} found in the image. Try a clearer image.`);
          }
        } catch (nlpError) {
          console.error('NLP processing error:', nlpError);
          setProcessingError(`Error processing text: ${nlpError.message}`);
        }
      } else {
        setProcessingError('No text was detected in the image. Try a clearer image with better lighting.');
      }
    } catch (error) {
      console.error('Processing error:', error);
      setProcessingError(`Error processing image: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
};
const cancelScanning = () => {
    console.log('Scanner.js: Canceling camera mode');
    
    // Immediately unmount the ScannerCamera component
    setIsCameraActive(false);
    
    // Reset all related state
    setNeedsDescription(false);
    setCapturedImage(null);
    setProcessingError('');
  };

  // Keep your existing bulk upload functionality
  const handleBulkUpload = (files) => {
    setBulkFiles(files);
    setNeedsDescription(true); // Use the same description screen as camera
  };
  
  const cancelBulkProcessing = () => {
    setIsBulkProcessing(false);
    setBulkFiles([]);
    setProcessingProgress(0);
  };
  
  const processBulkResults = (results) => {
    const newItems = results
      .filter(result => result.success && result.text)
      .map(result => ({
        id: Date.now() + Math.random(),
        description: scanDescription,
        text: result.text,
        timestamp: new Date().toLocaleString(),
        filename: result.filename
      }));
    
    setScannedItems(prevItems => [...newItems, ...prevItems]);
    setIsBulkProcessing(false);
    setBulkFiles([]);
    setProcessingProgress(0);
  };

  const deleteScannedItem = (id) => {
    setScannedItems(prevItems => {
      const updatedItems = prevItems.filter(item => item.id !== id);
      try {
        localStorage.setItem('scannedItems', JSON.stringify(updatedItems));
        console.log('Deleted item and updated localStorage');
      } catch (error) {
        console.error('Error saving to localStorage after delete:', error);
      }
      return updatedItems;
    });
  };

  return (
        
        <main className="">
          {isCameraActive ? (
            // Step 2: Show camera component to capture image
            <ScannerCamera 
              onCancel={cancelScanning}
              onCapture={handleImageCapture}
            />
          ) : isBulkProcessing ? (
            // Handle bulk processing (unchanged)
            <BulkProcessing
              files={bulkFiles}
              description={scanDescription}
              onCancel={cancelBulkProcessing}
              onComplete={processBulkResults}
              onProgress={setProcessingProgress}
            />
          ) : needsDescription ? (
            <div className="mt-6 max-w-4xl mx-auto px-3 sm:px-0">
            <div className="bg-white mt-3 rounded-lg shadow-md p-6 mb-6 ">
              <div className="mb-4">
                <h2 className="block mt-2 text-lg text-gray-700 font-semibold mb-1">What would you like to extract?</h2>
                <img 
                  src={capturedImage} 
                //   alt="Captured" 
                  className="w-full max-h-64 object-contain rounded-lg mb-4"
                />
                      {bulkFiles.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-blue-700 font-semibold">
            {bulkFiles.length} image{bulkFiles.length > 1 ? 's' : ''} selected for processing
          </p>
        </div>
      )}
              </div>
              
              <div className="mb-4">
                <label className="block mt-2 text-m text-gray-700 font-semibold mb-1">
                  Description:
                </label>
                <p className="text-sm text-gray-400 pb-3">
                  Enter a keyword (like 'serial', 'ID', 'IMEI') or specific text pattern you're looking for.
                </p>
                <input
                  type="text"
                  value={scanDescription}
                  onChange={(e) => setScanDescription(e.target.value)}
                  placeholder="E.g., Product ID, Serial Number, Barcode, Phone Number, Country"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="mb-4">
                <h3 className="block mt-2 text-m text-gray-700 font-semibold mb-1">Common Types:</h3>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['Serial Number', 'PO Number', 'Device ID', 'IMEI', 'ICCID', 'MAC Address'].map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setScanDescription(type)}
                      className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded text-sm"
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex justify-end space-x-4">
                <button
                        onClick={() => {
                            setNeedsDescription(false);
                            setCapturedImage(null);
                            setBulkFiles([]);
                            cancelScanning();
                          }}
 
                  className="px-4 py-2 border border-gray-300 text-gray-700 font-semibold rounded hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
 onClick={() => {
    if (capturedImage) {
      handleDescriptionSubmit(scanDescription);
    } else if (bulkFiles.length > 0) {
      // Use the correct function name here
      handleBulkProcessingStart(scanDescription, bulkFiles);
    }
  }}
  disabled={!scanDescription.trim()}
  className={`px-4 py-2 rounded ${
    scanDescription.trim()
      ? 'bg-blue-700 text-white font-semibold hover:bg-blue-800'
      : 'bg-blue-200 text-gray-400 font-semibold cursor-not-allowed'
  }`}
>
  Process
</button>
</div>
</div>
            </div>
          ) : (
            <>
              <ScannerForm 
                onStartScan={startScanning} 
                onImageUpload={handleImageUpload}
                onBulkUpload={handleBulkUpload}
              />
              
              {isProcessing && (
                <div className="my-8 flex flex-col items-center">
                  <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
                  <p className="text-gray-600">Processing uploaded image...</p>
                </div>
              )}
              
              {processingProgress > 0 && processingProgress < 100 && (
                <div className="my-8">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-blue-600 h-2.5 rounded-full" 
                      style={{ width: `${processingProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-center mt-2 text-gray-600">
                    Processing images: {processingProgress}% complete
                  </p>
                </div>
              )}
              
              {processingError && (
                <div className="my-4 bg-red-50 text-red-800 px-4 py-3 rounded-md text-center">
                  {processingError}
                </div>
              )}
              
              <ScannedResults 
                items={scannedItems} 
                onDelete={deleteScannedItem}
              />
            </>
          )}
                              {showSuccessPopup && (
          <SuccessPopup 
            message={successMessage} 
            onClose={() => {
                setShowSuccessPopup(false);
                scrollToResults();
              }} 
          />
        )}
        
        </main>

  );
}

export default Scanner;